/* eslint-disable */
import * as React from "react";
import { WithStyles, withStyles } from "@material-ui/styles";
import { FormattedMessage } from "react-intl";
import { Typography, List, ListItemText, ListItem, Button } from "@material-ui/core";
import { Scrollbars } from "react-custom-scrollbars";
import { ArrowDropDown, ArrowDropUp } from "@material-ui/icons";

import UpdatesCalendar from "./UpdatesCalendar";
import SectionsMenu from "./SectionsMenu";
import MainContent from "./MainContent";
import { helpPageSections, helpPageCalendar, helpPageMainContent } from "../constants/HelpPageConfig";
import { sectionItem } from "../constants/types";
import messages from "../constants/messages"
import { styles } from "../constants/styles";

type OwnProps = {
  selectedItemId: string;
  setSelectedItemId: (id: string) => void;
};

type Props = OwnProps & WithStyles<typeof styles>;

const HelpPageContent: React.FC<Props> = (props: Props) => {
  const itemHeights: number[] = [];
  const menuHeights: number[] = [];
  let listHeight: number = 0;

  const [listItemsHeights, setListItemsHeights] = React.useState<number[]>([]);
  const [menuHeight, setMenuHeight] = React.useState<number>(0);
  const [scrollHeight, setScrollHeight] = React.useState<number>(0);
  const [scrollTop, setScrollTop] = React.useState<number>(0);
  const [scrollForced, setScrollForced] = React.useState<boolean>(false);
  const [collapsedPanels, setCollapsedPanels] = React.useState<Set<string>>(new Set());
  const scrollbarRef = React.useRef(null);
  const { classes, selectedItemId, setSelectedItemId } = props;

  React.useEffect(() => {
    const itemHeightsSum = getSum(itemHeights);
    const menuHeightsSum = getSum(menuHeights);
    if (listHeight === scrollHeight) {
      setListItemsHeights(itemHeights);
      setMenuHeight(menuHeightsSum);
    }
  }, [scrollHeight]);

  React.useEffect(() => {
    const newSet = new Set(collapsedPanels);
    const wasCollapsed = newSet.delete(selectedItemId);
    setCollapsedPanels(newSet);
    if (wasCollapsed) {
      // timeout is required for calculating proper top offset after active panel expands
      setTimeout(() => {
        setScrollForced(true);
      }, 400);
    } else {
      setScrollForced(true);
    };
  }, [selectedItemId])

  const refCallbackMain = (element: any) => {
    if (element) {
      const height = element.offsetHeight;
      itemHeights.push(height);
    }
  };

  const getSum = (arr: number[]) => {
    return arr.reduce((acc, value) => acc + value, 0);
  };

  const refCallbackMenu = (element: any) => {
    if (element) {
      const height = element.offsetHeight;
      menuHeights.push(height);
    }
  };

  const refCallbackList = (element: any) => {
    if (element) {
      listHeight = element.offsetHeight
    }
  }

  const handleScrollFrame = (values) => {
    if (values.scrollHeight !== scrollHeight) {
      setScrollHeight(values.scrollHeight);
    }
    if (values.scrollTop !== scrollTop) {
      if (scrollForced) {
        setScrollForced(false)
      }
      setScrollTop(values.scrollTop)
    }
  };

  const scrollToTop = (top?: number) => {
    if (scrollbarRef.current) {
      if (top) {
        (scrollbarRef.current as Scrollbars)!.scrollTop(top);
      } else {
        (scrollbarRef.current as Scrollbars)!.scrollToTop();
      }
    }
  };

  const handleExpandCollapseAllPanels = (action: "expand" | "collapse") => () => {
    if (action === "expand") {
      setCollapsedPanels(new Set());
    } else {
      const panelsIds: string[] = [];
      helpPageMainContent.forEach(p => {
        p.sections.forEach(section => panelsIds.push(section.id));
      });
      helpPageCalendar.forEach(calendar => panelsIds.push(calendar.id));
      setCollapsedPanels(new Set(panelsIds));
    }
  };

  const selectedItem: sectionItem = helpPageSections.find((item) => item.id === selectedItemId) || helpPageSections[0];
  const headerMessage = helpPageSections
    .filter((item) => !item.level)
    .find((item) => item.showMenu === selectedItem.showMenu)!.name;
  if (scrollbarRef && listItemsHeights.length && selectedItemId) {
    const sectionsList = helpPageMainContent
      .map((item) => {
        const list: string[] = [];
        list.push(item.id);
        item.sections.forEach((section) => list.push(section.id));
        return list;
      })
      .flat();
    const selectedItem = sectionsList.find((item) => item === selectedItemId);
    if (selectedItem) {
      const selectedItemIndex = sectionsList.indexOf(selectedItem);
      const topItems = listItemsHeights.slice(0, selectedItemIndex);
      const topHeight: number = getSum(topItems) + menuHeight;
      if (topHeight !== scrollTop && scrollForced) {
        setScrollTop(topHeight)
      }
    } else {
      if (scrollTop && scrollForced) {
        setScrollTop(0)
      }
    }
  }

  scrollToTop(scrollTop)

  return (
    <>
      <div className={classes.headerContainer}>
        <Typography className={classes.contentHeader}>
          <FormattedMessage {...headerMessage} />
        </Typography>
        <div className={classes.headerActionsContainer}>
          <Button
            className={classes.headerActionButton}
            startIcon={<ArrowDropDown />}
            size="small"
            onClick={handleExpandCollapseAllPanels("expand")}
          >
            <FormattedMessage {...messages.expandAllButton} />
          </Button>
          <Button
            className={classes.headerActionButton}
            startIcon={<ArrowDropUp />}
            size="small"
            onClick={handleExpandCollapseAllPanels("collapse")}
          >
            <FormattedMessage {...messages.collapseAllButton} />
          </Button>
        </div>
      </div>
      <Scrollbars
        key={"HelpPage.Scrollbar.Main"}
        ref={scrollbarRef}
        autoHide
        hideTracksWhenNotNeeded
        onUpdate={handleScrollFrame}
      >
        {!selectedItem.showMenu && (
          <>
            {helpPageCalendar.map((item) => (
              <UpdatesCalendar 
                collapsedPanels={collapsedPanels}
                setCollapsedPanels={setCollapsedPanels}
                item={item} 
              />
            ))}
            <Typography className={classes.calendarCardPlainTypo}>
              <FormattedMessage {...messages.UpdateTimelineFootnote} />
            </Typography>
          </>
        )}
        {selectedItem.showMenu && (
          <List component="nav" dense className={classes.contentListRoot} ref={refCallbackList}>
            {helpPageSections
              .filter((item) => item.level)
              .map((item) => (
                <SectionsMenu item={item} setSelectedItemId={setSelectedItemId} refCallback={refCallbackMenu} />
              ))}
              <ListItem ref={refCallbackMenu} className={classes.contentListItemFiller}/>
            {helpPageMainContent.map((item) => (
              <MainContent 
                item={item} 
                refCallback={refCallbackMain} 
                scrollToTop={scrollToTop} 
                collapsedPanels={collapsedPanels}
                setCollapsedPanels={setCollapsedPanels}
              />
            ))}
          </List>
        )}
      </Scrollbars>
      {/* </div> */}
    </>
  );
};

export default withStyles(styles)(HelpPageContent);
